/**
 * Systems department items — final band of the home timeline (zone
 * 'systems', progress band 0.75–1). Visual layout: the systems grid backdrop
 * centred in the zone, with a small constellation of solid squares, rings and
 * gradient particles around it.
 *
 * Classification rule for this file: `zone: 'systems'` tag. (A ghost item,
 * forrestPhoto, was deleted 2026-07-17: its texture never existed in any
 * atlas, so it had never rendered — see the ghost-items commit.)
 *
 * Grouping is for HUMANS ONLY — creation order is owned by index.ts (see the
 * order contract there). Field semantics: see HomeItemConfig / the underlying
 * TimelineItemConfig.
 */
import type { HomeItemConfig } from './homeItems.types';

/**
 * The main systems grid (systems-grid.png), centred in the systems zone — the
 * department's defining backdrop.
 */
export const systemsGrid: HomeItemConfig = {
	disable: false,
	id: 'systems-grid',
	texture: 'systems-grid.png',
	relativeX: 0.5,
	relativeXMobile: 0.5,
	relativeY: 0.45,
	easePower: 3,
	alpha: 1e-6,
	parallaxOffsetPower: 0,
	parallaxOffsetBase: 0,
	parallaxPowerMouse: 0,
	autoAlpha: false,
	autoRange: 0.9,
	zone: 'systems'
};

/**
 * Solid square particle left of the systems grid (ref 'Left bigger'). The id
 * reads 'spacesp' (copy-paste ancestry) but it is zoned to systems.
 */
export const systemsSquareLeft: HomeItemConfig = {
	disable: false,
	id: 'spacesp',
	ref: 'Left bigger',
	texture: 'solid-square.png',
	zone: 'systems',
	relativeX: 0.47,
	relativeY: 0.5,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.08,
	parallaxPowerMouse: 0.02,
	autoScale: false,
	startScale: 0.0001,
	minScale: 12,
	maxScale: 12,
	autoRange: 10,
	repell: false,
	alpha: 0.0001,
	hideAtWidth: 0
};

/**
 * Faded gradient particle low in the systems zone (ref 'righttop smaller').
 * `texturex` records the solid-square.png texture it used before being
 * re-pointed — renamed-off and inert.
 */
export const systemsP1: HomeItemConfig = {
	disable: false,
	id: 'systemsp1',
	ref: 'righttop smaller',
	texturex: 'solid-square.png',
	texture: 'gradient-particle.png',
	zone: 'systems',
	relativeX: 0.2,
	relativeXMobile: 0.15,
	relativeY: 1,
	relativeYMobile: 0.72,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.07,
	parallaxPowerMouse: 0.03,
	autoScale: false,
	startScale: 0.0001,
	minScale: 6,
	maxScale: 6,
	autoRange: 0.3,
	repell: false,
	alpha: 0.05,
	alphaMobile: 0.09,
	hideAtWidth: 0
};

/**
 * Solid ring particle (solid-ring.png) in the systems zone (ref 'righttop
 * smaller'). `texturex` is a renamed-off leftover.
 */
export const systemsP2: HomeItemConfig = {
	disable: false,
	id: 'systemsp2',
	ref: 'righttop smaller',
	texture: 'solid-ring.png',
	texturex: 'gradient-particle.png',
	zone: 'systems',
	relativeX: 0.47,
	relativeXMobile: 0.6,
	relativeY: 0.8,
	relativeYMobile: 0.78,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.08,
	parallaxPowerMouse: 0.03,
	autoScale: false,
	startScale: 0.0001,
	minScale: 3,
	maxScale: 3,
	autoRange: 0.3,
	repell: false,
	alpha: 0.085,
	alphaMobile: 0.11,
	hideAtWidth: 0
};

/**
 * Gradient particle on the right flank of the systems zone (relativeX 1.2 —
 * outside the content box). There is no systemsP3 in the data; the numbering
 * gap comes from the source ids.
 */
export const systemsP4: HomeItemConfig = {
	disable: false,
	id: 'systemsp4',
	ref: 'righttop smaller',
	texturex: 'solid-square.png',
	texture: 'gradient-particle.png',
	zone: 'systems',
	relativeX: 1.2,
	relativeXMobile: 0.9,
	relativeY: 0.6,
	relativeYMobile: 0.85,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.08,
	parallaxPowerMouse: 0.13,
	autoScale: false,
	startScale: 0.0001,
	minScale: 16,
	maxScale: 16,
	autoRange: 0.3,
	repell: false,
	alpha: 0.07,
	alphaMobile: 0.08,
	hideAtWidth: 900
};

/**
 * Solid ring particle at the left edge of the systems zone.
 */
export const systemsP5: HomeItemConfig = {
	disable: false,
	id: 'systemsp5',
	ref: 'righttop smaller',
	texture: 'solid-ring.png',
	texturex: 'gradient-particle.png',
	zone: 'systems',
	relativeX: 0,
	relativeXMobile: 0.07,
	relativeY: 0.07,
	relativeYMobile: 0.13,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.06,
	parallaxPowerMouse: 0.06,
	autoScale: false,
	startScale: 0.0001,
	minScale: 6,
	maxScale: 6,
	autoRange: 0.3,
	repell: false,
	alpha: 0.07,
	alphaMobile: 0.11,
	hideAtWidth: 0
};

/**
 * Gradient particle right-of-centre in the systems zone.
 */
export const systemsP6: HomeItemConfig = {
	disable: false,
	id: 'systemsp6',
	ref: 'righttop smaller',
	texture: 'gradient-particle.png',
	texturex: 'gradient-particle.png',
	zone: 'systems',
	relativeX: 0.8,
	relativeY: 0,
	easePower: 10,
	parallaxOffsetBase: 1,
	parallaxOffsetPower: 0.06,
	parallaxPowerMouse: 0.06,
	autoScale: false,
	startScale: 0.0001,
	minScale: 40,
	maxScale: 40,
	autoRange: 0.3,
	repell: false,
	alpha: 0.07,
	alphaMobile: 0.08,
	hideAtWidth: 0
};
